/**
 * Batch API Hook
 * Combines multiple API requests into a single HTTP request for better performance
 *
 * Benefits:
 * - Reduces HTTP round-trips (saves ~100-200ms per additional request)
 * - Single loading state for multiple data sources
 * - Atomic error handling
 *
 * Usage:
 * const { data, loading, error, refetch } = useBatchAPI([
 *   { id: 'stats', path: '/api/copytrading/stats' },
 *   { id: 'following', path: '/api/copytrading/following' },
 * ]);
 * // data.stats, data.following
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface BatchRequest {
  id: string;
  path: string;
}

interface BatchResponse<T = any> {
  status: number;
  data?: T;
  error?: string;
}

interface UseBatchAPIResult<T extends Record<string, any>> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Global in-flight map: dedup concurrent batch requests with same request set
const inflightBatch = new Map<string, Promise<{ data: any; error: string | null }>>();
const batchCache = new Map<string, { expiresAt: number; result: { data: any; error: string | null } }>();
const BATCH_CACHE_TTL_MS = 10_000;
const MAX_BATCH_CACHE_ENTRIES = 100;

function batchKey(requests: BatchRequest[]): string {
  return requests.map(r => r.id + ':' + r.path).sort().join('|');
}

function pruneBatchCache(): void {
  const now = Date.now();
  for (const [key, entry] of batchCache.entries()) {
    if (entry.expiresAt <= now) {
      batchCache.delete(key);
    }
  }

  while (batchCache.size > MAX_BATCH_CACHE_ENTRIES) {
    const oldestKey = batchCache.keys().next().value;
    if (!oldestKey) break;
    batchCache.delete(oldestKey);
  }
}

/**
 * Execute a batch API request (with in-flight deduplication)
 */
export async function batchFetch<T extends Record<string, any>>(
  requests: BatchRequest[]
): Promise<{ data: T | null; error: string | null }> {
  const key = batchKey(requests);

  const cached = batchCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.result as { data: T | null; error: string | null };
  }

  // If an identical batch is already in-flight, piggyback on it
  const inflight = inflightBatch.get(key);
  if (inflight) return inflight as Promise<{ data: T | null; error: string | null }>;

  const promise = doBatchFetch<T>(requests)
    .then((result) => {
      if (result.data && !result.error) {
        pruneBatchCache();
        batchCache.set(key, {
          expiresAt: Date.now() + BATCH_CACHE_TTL_MS,
          result,
        });
      }
      return result;
    })
    .finally(() => {
      inflightBatch.delete(key);
    });

  inflightBatch.set(key, promise);
  return promise;
}

async function doBatchFetch<T extends Record<string, any>>(
  requests: BatchRequest[]
): Promise<{ data: T | null; error: string | null }> {
  try {
    const response = await fetch('/api/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requests }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: errorData.error || `Batch request failed: ${response.status}`,
      };
    }

    const result = await response.json();

    // Transform responses into a flat object
    const data: Record<string, any> = {};
    let hasError = false;
    let errorMessage = '';

    for (const [id, resp] of Object.entries(result.responses as Record<string, BatchResponse>)) {
      if (resp.status >= 200 && resp.status < 300) {
        data[id] = resp.data;
      } else {
        hasError = true;
        errorMessage = resp.error || `Request ${id} failed`;
      }
    }

    if (hasError && Object.keys(data).length === 0) {
      return { data: null, error: errorMessage };
    }

    return { data: data as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Batch request failed',
    };
  }
}

/**
 * Hook for batch API requests
 */
export function useBatchAPI<T extends Record<string, any>>(
  requests: BatchRequest[],
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
): UseBatchAPIResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const { enabled = true, refetchInterval } = options || {};

  const fetchData = useCallback(async () => {
    if (!enabled || requests.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await batchFetch<T>(requests);

    if (mountedRef.current) {
      setData(result.data);
      setError(result.error);
      setLoading(false);
    }
  }, [requests, enabled]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  // Refetch interval — pauses when tab is hidden to avoid phantom requests
  useEffect(() => {
    if (!refetchInterval || !enabled) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (!interval) interval = setInterval(fetchData, refetchInterval);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };

    const onVisibility = () => {
      if (document.hidden) stop(); else start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refetchInterval, enabled, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

/**
 * Prefetch batch data (for preloading on hover, etc.)
 */
export function prefetchBatch(requests: BatchRequest[]): void {
  batchFetch(requests).catch(() => {
    // Silently fail prefetch
  });
}

export default useBatchAPI;
